import { Injectable, inject } from '@angular/core';
import { Auth } from '@angular/fire/auth';
import {
  DocumentData,
  DocumentReference,
  getError,
  httpsCallable,
  serialise,
  SerialisedData,
  snapshot,
} from '@principle-theorem/shared';
import { getAuthErrorMessage } from './auth-error-messages';
import {
  MultiFactorError,
  MultiFactorInfo,
  MultiFactorResolver,
  PhoneAuthProvider,
  PhoneMultiFactorGenerator,
  PhoneMultiFactorInfo,
  RecaptchaVerifier,
  TotpMultiFactorGenerator,
  TotpMultiFactorInfo,
  TotpSecret,
  UserCredential,
  getMultiFactorResolver,
  multiFactor,
} from 'firebase/auth';
import {
  IMultifactorEnrolmentResult,
  IMultifactorVerificationContext,
  MultifactorEnrolmentState,
  MultifactorFactorId,
  MultifactorMethod,
} from './multifactor.types';

export interface IDisableMultifactorForUsersRequest {
  organisationRef: DocumentReference;
  userRefs: DocumentReference[];
}

export interface IDisableMultifactorForUsersResponse {
  success: true;
  wipedCount: number;
  failedUids: string[];
}

@Injectable({ providedIn: 'root' })
export class MultifactorService {
  private _auth = inject(Auth);

  async enrolPhone(
    phoneNumber: string,
    recaptchaVerifier: RecaptchaVerifier
  ): Promise<string> {
    const user = this._auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found.');
    }
    const session = await multiFactor(user).getSession();
    const provider = new PhoneAuthProvider(this._auth);
    const verificationId = await provider.verifyPhoneNumber(
      { phoneNumber, session },
      recaptchaVerifier
    );
    return verificationId;
  }

  async confirmPhoneEnrolment(
    verificationId: string,
    verificationCode: string,
    displayName?: string
  ): Promise<IMultifactorEnrolmentResult> {
    try {
      const user = this._auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found.');
      }
      const credential = PhoneAuthProvider.credential(
        verificationId,
        verificationCode
      );
      const assertion = PhoneMultiFactorGenerator.assertion(credential);
      await multiFactor(user).enroll(assertion, displayName ?? 'Phone');
      return { success: true, method: MultifactorMethod.Phone };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA phone enrolment failed:', getError(error));
      return {
        success: false,
        method: MultifactorMethod.Phone,
        error: getAuthErrorMessage(error),
      };
    }
  }

  async enrolTotp(): Promise<{ secret: TotpSecret; qrCodeUrl: string }> {
    const user = this._auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found.');
    }
    const session = await multiFactor(user).getSession();
    const secret = await TotpMultiFactorGenerator.generateSecret(session);
    const qrCodeUrl = secret.generateQrCodeUrl(
      user.email ?? user.uid,
      'Principle'
    );
    return { secret, qrCodeUrl };
  }

  async confirmTotpEnrolment(
    secret: TotpSecret,
    verificationCode: string,
    displayName?: string
  ): Promise<IMultifactorEnrolmentResult> {
    try {
      const user = this._auth.currentUser;
      if (!user) {
        throw new Error('No authenticated user found.');
      }
      const assertion = TotpMultiFactorGenerator.assertionForEnrollment(
        secret,
        verificationCode
      );
      await multiFactor(user).enroll(assertion, displayName ?? 'Authenticator');
      return { success: true, method: MultifactorMethod.Totp };
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('MFA TOTP enrolment failed:', getError(error));
      return {
        success: false,
        method: MultifactorMethod.Totp,
        error: getAuthErrorMessage(error),
      };
    }
  }

  resolveMultiFactorSignIn(
    error: MultiFactorError
  ): IMultifactorVerificationContext {
    const resolver = getMultiFactorResolver(this._auth, error);
    return { resolver, hints: resolver.hints };
  }

  async verifyWithPhone(
    resolver: MultiFactorResolver,
    hint: PhoneMultiFactorInfo,
    recaptchaVerifier: RecaptchaVerifier
  ): Promise<string> {
    const provider = new PhoneAuthProvider(this._auth);
    return provider.verifyPhoneNumber(
      { multiFactorHint: hint, session: resolver.session },
      recaptchaVerifier
    );
  }

  async completePhoneVerification(
    resolver: MultiFactorResolver,
    verificationId: string,
    code: string
  ): Promise<UserCredential> {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    const assertion = PhoneMultiFactorGenerator.assertion(credential);
    return resolver.resolveSignIn(assertion);
  }

  async verifyWithTotp(
    resolver: MultiFactorResolver,
    hint: TotpMultiFactorInfo,
    code: string
  ): Promise<UserCredential> {
    const assertion = TotpMultiFactorGenerator.assertionForSignIn(
      hint.uid,
      code
    );
    return resolver.resolveSignIn(assertion);
  }

  getEnrolledFactors(): MultiFactorInfo[] {
    const user = this._auth.currentUser;
    if (!user) {
      return [];
    }
    return multiFactor(user).enrolledFactors;
  }

  getEnrolmentState(): MultifactorEnrolmentState {
    const factors = this.getEnrolledFactors();
    const hasPhone = factors.some(
      (factor) => factor.factorId === MultifactorFactorId.Phone
    );
    const hasTotp = factors.some(
      (factor) => factor.factorId === MultifactorFactorId.Totp
    );

    if (hasPhone && hasTotp) {
      return MultifactorEnrolmentState.EnrolledBoth;
    }
    if (hasPhone) {
      return MultifactorEnrolmentState.EnrolledPhone;
    }
    if (hasTotp) {
      return MultifactorEnrolmentState.EnrolledTotp;
    }
    return MultifactorEnrolmentState.NotEnrolled;
  }

  async unenrol(factorUid: string): Promise<void> {
    const user = this._auth.currentUser;
    if (!user) {
      throw new Error('No authenticated user found.');
    }
    await multiFactor(user).unenroll(factorUid);
  }

  isEmailVerified(): boolean {
    return this._auth.currentUser?.emailVerified ?? false;
  }

  async disableMultifactorForUsers<
    User extends DocumentData,
    Organisation extends DocumentData,
  >(
    userRefs: DocumentReference<User>[],
    organisationRef: DocumentReference<Organisation>
  ): Promise<IDisableMultifactorForUsersResponse> {
    return snapshot(
      httpsCallable<
        SerialisedData<IDisableMultifactorForUsersRequest>,
        IDisableMultifactorForUsersResponse
      >('http-auth-disableMultifactorForUsers')(
        serialise({ userRefs, organisationRef })
      )
    );
  }
}
